import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Segment, Header, Table, Button, Dropdown, Message, Label } from 'semantic-ui-react';
// Shared with the stream timeline, so both screens phrase a delay identically.
import { describeDelay } from '../Posts/PostsScheduled';

/**
 * Reposting rules — Phase 6.11.
 *
 * A replicator copies each post published into one stream over to another, after a delay. The
 * copy is a **real post**: its own views, dwell time, per-image counts and likes, so the two
 * audiences can be measured apart.
 *
 * Needs MANAGE of **both** streams. Reposting out of a stream republishes somebody else's posts
 * to a new audience, which is a decision about the source as much as the destination.
 */

const GET_REPLICATORS = gql`
  query Replicators {
    replicators {
      id sourceStreamId sourceStreamName targetStreamId targetStreamName delayMinutes repostedCount
    }
    streams { id name canManage }
  }
`;

const CREATE_REPLICATOR = gql`
  mutation CreateReplicator($sourceStreamId: Int!, $targetStreamId: Int!, $delayMinutes: Int!) {
    createReplicator(sourceStreamId: $sourceStreamId, targetStreamId: $targetStreamId, delayMinutes: $delayMinutes)
  }
`;

const REMOVE_REPLICATOR = gql`
  mutation RemoveReplicator($replicatorId: Int!) {
    removeReplicator(replicatorId: $replicatorId)
  }
`;

/**
 * Delays, in minutes — 1 to 1440, matching the check constraint on the table.
 *
 * "Straight away" is one minute rather than zero, because one minute is the ticker's interval
 * and therefore the shortest delay the system can actually honour — calling it zero would be a
 * label the behaviour does not match.
 */
const DELAYS = [
  { key: 1, text: 'straight away', value: 1 },
  { key: 5, text: '5 min later', value: 5 },
  { key: 15, text: '15 min later', value: 15 },
  { key: 30, text: '30 min later', value: 30 },
  ...Array.from({ length: 24 }, (_, i) => ({
    key: (i + 1) * 60,
    text: i === 0 ? '1 hour later' : `${i + 1} hours later`,
    value: (i + 1) * 60,
  })),
];

export const Replicators = () => {
  const { data, loading, refetch } = useQuery(GET_REPLICATORS, { fetchPolicy: 'network-only' });
  const [createReplicator, { loading: creating }] = useMutation(CREATE_REPLICATOR);
  const [removeReplicator] = useMutation(REMOVE_REPLICATOR);

  const [adding, setAdding] = useState(false);
  const [source, setSource] = useState<number | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  // Two hours, in minutes — the old default, unchanged in meaning.
  const [delay, setDelay] = useState<number>(120);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return null;
  }

  const rules = data?.replicators ?? [];
  const manageable = (data?.streams ?? []).filter((s: any) => s.canManage);

  // Fewer than two manageable streams means there is nowhere to repost between, and the whole
  // section would be a form that cannot be completed.
  if (manageable.length < 2 && rules.length === 0) {
    return null;
  }

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? failure);
    }
  };

  return (
    <Segment>
      <Header as="h3">Reposting</Header>
      <p style={{ color: '#767676' }}>
        Automatically repost one stream's posts into another, a few hours later. Each repost is
        its own post, so the two audiences get their own view counts.
      </p>

      {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}

      {rules.length === 0
        ? <p style={{ color: '#767676' }}>No reposting rules yet.</p>
        : (
          <Table compact>
            <Table.Body>
              {rules.map((r: any) => (
                <Table.Row key={r.id}>
                  <Table.Cell>
                    <strong>{r.sourceStreamName}</strong>
                    <span style={{ color: '#767676' }}> &rarr; </span>
                    <strong>{r.targetStreamName}</strong>
                    <div style={{ color: '#767676', fontSize: '0.85em' }}>
                      {describeDelay(r.delayMinutes)}
                      {r.repostedCount > 0 && ` · ${r.repostedCount} reposted so far`}
                    </div>
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    <Button
                      size="tiny"
                      onClick={() => act(
                        () => removeReplicator({ variables: { replicatorId: r.id } }),
                        'Could not stop that rule.',
                      )}
                    >
                      Stop
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

      {rules.length > 0 && (
        <p style={{ color: '#767676', fontSize: '0.85em' }}>
          Stopping a rule leaves posts it has already reposted where they are — people have
          already seen them.
        </p>
      )}

      {adding
        ? (
          <Segment secondary>
            <Dropdown
              selection
              placeholder="Posts from…"
              style={{ marginRight: '0.5em', minWidth: '13em' }}
              value={source ?? undefined}
              options={manageable.map((s: any) => ({ key: s.id, text: s.name, value: s.id }))}
              onChange={(_: any, d: any) => setSource(d.value as number)}
            />
            <Dropdown
              selection
              placeholder="…get reposted into"
              style={{ marginRight: '0.5em', minWidth: '13em' }}
              value={target ?? undefined}
              // A stream cannot repost into itself: every post would copy forever.
              options={manageable
                .filter((s: any) => s.id !== source)
                .map((s: any) => ({ key: s.id, text: s.name, value: s.id }))}
              onChange={(_: any, d: any) => setTarget(d.value as number)}
            />
            <Dropdown
              selection
              compact
              style={{ marginRight: '0.5em' }}
              value={delay}
              options={DELAYS}
              onChange={(_: any, d: any) => setDelay(d.value as number)}
            />
            <Button
              primary
              loading={creating}
              disabled={creating || source == null || target == null}
              onClick={async () => {
                await act(
                  () => createReplicator({
                    variables: { sourceStreamId: source, targetStreamId: target, delayMinutes: delay },
                  }),
                  'Could not create that rule.',
                );
                setAdding(false);
                setSource(null);
                setTarget(null);
              }}
            >
              Start reposting
            </Button>
            <Button onClick={() => { setAdding(false); setSource(null); setTarget(null); }}>
              Cancel
            </Button>
            <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.5em' }}>
              Only posts published from now on are reposted — this will not go back through the
              stream's history.
            </p>
          </Segment>
        )
        : manageable.length >= 2 && (
          <Button size="small" onClick={() => setAdding(true)}>Add a reposting rule</Button>
        )}
    </Segment>
  );
};
